import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  Paper,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Alert,
  AlertTitle,
  Tooltip,
  TooltipProps,
  tooltipClasses
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import styled from '@emotion/styled';
import {ReactElement} from 'react';
import {EmailItem, NotificationItem} from '../types';
import {
  RECEIVE_MAIL_TYPE,
  RECEIVE_MAIL_OPTIONS,
  ReceiveMailType
} from '../constants/alertSettingsConfig';

interface HelpTooltipProps extends Omit<TooltipProps, 'children'> {
  title: string;
  children?: ReactElement;
}

const HelpTooltip = styled(({className, ...props}: HelpTooltipProps) => (
  <Tooltip {...props} classes={{popper: className}}>
    <HelpOutlineIcon />
  </Tooltip>
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: '14px',
    whiteSpace: 'pre-line'
  }
}));

interface AlertSettingsStep2Props {
  notifications: NotificationItem[];
  emails: EmailItem[];
  alertId: string;
  t: (key: string) => string;
  // インライン編集用
  handleInlineSendSettingChange: (index: number, receiveMailType: ReceiveMailType) => void;
  handleInlineDelete: (index: number) => void;
  // 通知先追加ダイアログ用
  addDialogOpen: boolean;
  handleOpenAddDialog: () => void;
  handleCloseAddDialog: () => void;
  handleInlineAdd: () => void;
  newEmail: string;
  setNewEmail: (value: string) => void;
  newReceiveMailType: ReceiveMailType;
  setNewReceiveMailType: (value: ReceiveMailType) => void;
  // バッチ操作用
  selectedIndices: number[];
  handleToggleSelect: (index: number) => void;
  handleToggleSelectAll: () => void;
  handleBatchDelete: () => void;
  handleBatchSendSettingChange: (receiveMailType: ReceiveMailType) => void;
}

export const AlertSettingsRecipients = ({
  notifications,
  emails,
  alertId,
  t,
  handleInlineSendSettingChange,
  handleInlineDelete,
  addDialogOpen,
  handleOpenAddDialog,
  handleCloseAddDialog,
  handleInlineAdd,
  newEmail,
  setNewEmail,
  newReceiveMailType,
  setNewReceiveMailType,
  selectedIndices,
  handleToggleSelect,
  handleToggleSelectAll,
  handleBatchDelete,
  handleBatchSendSettingChange
}: AlertSettingsStep2Props) => {
  return (
    <>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 2}}>
        <Typography variant="h6">{t('通知先の設定')}</Typography>
        <HelpTooltip
          title={t(
            '「通知先を追加」ボタンから通知先の追加ができます。通知先が0件の場合、アラートは実行されますが通知は送信されません。設定は「保存」ボタンを押すまで保存されません。'
          )}
        />
      </Box>

      {/* 新規登録時のガイダンス */}
      {alertId === '-1' && (
        <Alert severity="info" sx={{mb: 3}}>
          <AlertTitle>{t('新規アラート設定')}</AlertTitle>
          <Typography variant="body2">
            {t('message.新規アラートを作成します。通知先を少なくとも1件追加してください。')}
          </Typography>
        </Alert>
      )}

      {/* ボタンエリア - バッチ操作(左)と追加ボタン(右) */}
      <Box
        sx={{
          marginBottom: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        {/* 左側: バッチ操作ボタン */}
        <Box sx={{display: 'flex', gap: 1}}>
          {selectedIndices.length > 0 && (
            <>
              <Button variant="outlined" color="secondary" size="small" onClick={handleBatchDelete}>
                {t('選択項目を削除')} ({selectedIndices.length})
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleBatchSendSettingChange(RECEIVE_MAIL_TYPE.ALL)}
              >
                {t('選択項目を毎回送信に変更')}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleBatchSendSettingChange(RECEIVE_MAIL_TYPE.ABNORMAL_ONLY)}
              >
                {t('選択項目を異常時のみに変更')}
              </Button>
            </>
          )}
        </Box>

        {/* 右側: 追加ボタン */}
        <Button variant="contained" color="primary" onClick={handleOpenAddDialog} size="small">
          {t('通知先を追加')}
        </Button>
      </Box>

      {/* 通知先一覧テーブル */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={
                    notifications.length > 0 && selectedIndices.length === notifications.length
                  }
                  indeterminate={
                    selectedIndices.length > 0 && selectedIndices.length < notifications.length
                  }
                  onChange={handleToggleSelectAll}
                />
              </TableCell>
              <TableCell>{t('メールアドレス')}</TableCell>
              <TableCell>{t('ユーザー名')}</TableCell>
              <TableCell align="center">{t('送信設定')}</TableCell>
              <TableCell align="center">{t('操作')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {notifications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 5,
                      gap: 2
                    }}
                  >
                    <WarningIcon sx={{fontSize: 48, color: 'warning.main'}} />
                    <Typography variant="h6" color="text.secondary">
                      {t('通知先が登録されていません')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('「通知先を追加」ボタンから通知先を追加してください')}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              notifications.map((notification: any, index: number) => {
                const emailData = emails.find(e => e.id === notification.email);
                return (
                  <TableRow key={index} selected={selectedIndices.includes(index)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIndices.includes(index)}
                        onChange={() => handleToggleSelect(index)}
                      />
                    </TableCell>
                    <TableCell>{emailData?.email || ''}</TableCell>
                    <TableCell>{emailData?.id || ''}</TableCell>
                    <TableCell align="center">
                      <Select
                        value={notification.receiveMailType}
                        onChange={e =>
                          handleInlineSendSettingChange(
                            index,
                            Number(e.target.value) as ReceiveMailType
                          )
                        }
                        size="small"
                        sx={{minWidth: 160}}
                      >
                        {RECEIVE_MAIL_OPTIONS.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        onClick={() => handleInlineDelete(index)}
                        size="small"
                        color="error"
                        variant="outlined"
                      >
                        {t('削除')}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 通知先追加ダイアログ */}
      <Dialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        fullWidth
        maxWidth="sm"
        disableRestoreFocus
        disableEnforceFocus
      >
        <DialogTitle>{t('通知先の追加')}</DialogTitle>

        <DialogContent>
          {/* メールアドレス検索 */}
          <div style={{marginBottom: 24, marginTop: 16}}>
            <Typography variant="subtitle1" marginBottom={1}>
              {t('メールアドレス')}
            </Typography>
            <Autocomplete
              options={emails.map(email => email.email)}
              value={newEmail || null}
              onChange={(event, newValue) => {
                setNewEmail(newValue || '');
              }}
              inputValue={newEmail}
              onInputChange={(event, newInputValue) => setNewEmail(newInputValue)}
              renderInput={params => (
                <TextField
                  {...params}
                  label={t('メールアドレス検索')}
                  variant="outlined"
                  fullWidth
                  placeholder={t('メールアドレスを入力または選択')}
                />
              )}
            />
          </div>

          {/* 送信設定 */}
          <div>
            <Typography variant="subtitle1" marginBottom={1}>
              {t('送信設定')}
            </Typography>
            <Select
              value={newReceiveMailType}
              onChange={e => setNewReceiveMailType(Number(e.target.value) as ReceiveMailType)}
              fullWidth
              variant="outlined"
            >
              {RECEIVE_MAIL_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        </DialogContent>

        <DialogActions sx={{padding: 2}}>
          <Button onClick={handleCloseAddDialog}>{t('キャンセル')}</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleInlineAdd}
            disabled={!newEmail}
          >
            {t('追加')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
